/**
 * Route guard for `/selector/*` — Wave 2.35 FE, TASK-107a.
 *
 * Sits *inside* the `<ProtectedRoute>` subtree, so the user is guaranteed to be
 * authenticated by the time this runs. Layered RBAC rules:
 *
 *  - role === 'selector' → render children.
 *  - any other role (admin / super_admin / client_*) → redirect to `/`.
 *
 * The redirect target intentionally points at `/` so the upstream
 * `<RoleHomeRedirect>` can dispatch admins to `/dashboard` without this guard
 * needing to know the admin landing path.
 *
 * `user` can be momentarily `null` right after silent-refresh resolves — return
 * `null` (loader-equivalent) until the auth context settles. The outer
 * `<ProtectedRoute>` already renders a loader during bootstrap so the user
 * never sees a blank flash longer than one render.
 */

import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';

export function SelectorOnlyRoute({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role !== 'selector') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
